import { useEffect, useRef, memo, useCallback } from 'react';

import { cn } from '@/lib/utils';

interface EditableTextProps {
  initialValue: string;
  onSubmit: (value: string) => Promise<void>;
  className?: string;
}

export const EditableText = memo(({ initialValue, onSubmit, className }: EditableTextProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  // Only update content when initialValue changes
  useEffect(() => {
    if (spanRef.current && spanRef.current.textContent !== initialValue) {
      spanRef.current.textContent = initialValue;
    }
  }, [initialValue]);

  const handleSubmit = useCallback(async () => {
    if (!spanRef.current) return;

    try {
      await onSubmit(spanRef.current.textContent || '');
    } catch {
      // On error, revert to previous value
      spanRef.current.textContent = initialValue;
    }
  }, [initialValue, onSubmit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (spanRef.current) {
        spanRef.current.textContent = initialValue;
      }
      return;
    }
  };

  return (
    <span
      ref={spanRef}
      aria-label="Title"
      className={cn('font-medium text-2xl', className)}
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      onBlur={handleSubmit}
      onKeyDown={handleKeyDown}
    >
      {initialValue}
    </span>
  );
});
